'use client';

import { useState } from 'react';
import { Calendar, Loader2, Clock, MapPin, Video, Users as UsersIcon, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Input } from '@/components/ui/forms/input';
import { Textarea } from '@/components/ui/forms/textarea';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/forms/select';
import { Label } from '@/components/ui/forms/label';
import { Switch } from '@/components/ui/forms/switch';

type InterviewType = 'PHONE_SCREEN' | 'TECHNICAL' | 'BEHAVIORAL' | 'ONSITE' | 'FINAL';

interface ScheduleInterviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (interview: {
    type: InterviewType;
    scheduledAt: Date;
    duration: number;
    isRemote: boolean;
    location?: string;
    meetingLink?: string;
    interviewerNames: string[];
    notes?: string;
  }) => Promise<void>;
  contactName?: string;
  companyName?: string;
}

export function ScheduleInterviewDialog({
  open,
  onOpenChange,
  onSave,
  contactName,
  companyName,
}: ScheduleInterviewDialogProps) {
  const [interviewType, setInterviewType] = useState<InterviewType>('PHONE_SCREEN');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [duration, setDuration] = useState('60');
  const [isRemote, setIsRemote] = useState(true);
  const [location, setLocation] = useState('');
  const [meetingLink, setMeetingLink] = useState('');
  const [interviewers, setInterviewers] = useState<string[]>(['']);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const handleAddInterviewer = () => {
    setInterviewers([...interviewers, '']);
  };

  const handleRemoveInterviewer = (index: number) => {
    setInterviewers(interviewers.filter((_, i) => i !== index));
  };

  const handleInterviewerChange = (index: number, value: string) => {
    const updated = [...interviewers];
    updated[index] = value;
    setInterviewers(updated);
  };

  const handleSave = async () => {
    if (!scheduledDate || !scheduledTime) {
      return;
    }

    // Combine date and time
    const dateTimeString = `${scheduledDate}T${scheduledTime}`;
    const scheduledAt = new Date(dateTimeString);

    // Filter out empty interviewer names
    const filteredInterviewers = interviewers.filter(name => name.trim() !== '');

    setIsSaving(true);
    try {
      await onSave({
        type: interviewType,
        scheduledAt,
        duration: parseInt(duration, 10),
        isRemote,
        location: location.trim() || undefined,
        meetingLink: meetingLink.trim() || undefined,
        interviewerNames: filteredInterviewers,
        notes: notes.trim() || undefined,
      });
      // Reset form
      setInterviewType('PHONE_SCREEN');
      setScheduledDate('');
      setScheduledTime('');
      setDuration('60');
      setIsRemote(true);
      setLocation('');
      setMeetingLink('');
      setInterviewers(['']);
      setNotes('');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to schedule interview:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    setInterviewType('PHONE_SCREEN');
    setScheduledDate('');
    setScheduledTime('');
    setDuration('60');
    setIsRemote(true);
    setLocation('');
    setMeetingLink('');
    setInterviewers(['']);
    setNotes('');
    onOpenChange(false);
  };

  const isFormValid = scheduledDate && scheduledTime;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeading
          title="Schedule Interview"
          description={`Schedule an interview with ${contactName || 'this contact'}${
            companyName ? ` at ${companyName}` : ''
          }.`}
        />

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="interview-type" className="text-overline">
              Interview Type <span className="text-destructive">*</span>
            </Label>
            <Select
              value={interviewType}
              onValueChange={(value) => setInterviewType(value as InterviewType)}
            >
              <SelectTrigger id="interview-type" className="border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PHONE_SCREEN">Phone Screen</SelectItem>
                <SelectItem value="TECHNICAL">Technical Interview</SelectItem>
                <SelectItem value="BEHAVIORAL">Behavioral Interview</SelectItem>
                <SelectItem value="ONSITE">Onsite Interview</SelectItem>
                <SelectItem value="FINAL">Final Interview</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduled-date" className="text-overline">
                Date <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="scheduled-date"
                  type="date"
                  value={scheduledDate}
                  onChange={(e) => setScheduledDate(e.target.value)}
                  className="border pl-10"
                  disabled={isSaving}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="scheduled-time" className="text-overline">
                Time <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="scheduled-time"
                  type="time"
                  value={scheduledTime}
                  onChange={(e) => setScheduledTime(e.target.value)}
                  className="border pl-10"
                  disabled={isSaving}
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration" className="text-overline">
              Duration (minutes)
            </Label>
            <Select
              value={duration}
              onValueChange={setDuration}
            >
              <SelectTrigger id="duration" className="border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 minutes</SelectItem>
                <SelectItem value="45">45 minutes</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between space-x-2 p-3 border border-border rounded-sm bg-muted/20">
            <div className="space-y-0.5">
              <Label htmlFor="is-remote" className="text-overline">
                Remote Interview
              </Label>
              <p className="text-caption text-muted-foreground">
                This interview will be conducted remotely
              </p>
            </div>
            <Switch
              id="is-remote"
              checked={isRemote}
              onCheckedChange={setIsRemote}
              disabled={isSaving}
            />
          </div>

          {isRemote ? (
            <div className="space-y-2">
              <Label htmlFor="meeting-link" className="text-overline">
                Meeting Link
              </Label>
              <div className="relative">
                <Video className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="meeting-link"
                  type="url"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder="https://zoom.us/j/..."
                  className="border pl-10"
                  disabled={isSaving}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="location" className="text-overline">
                Location
              </Label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Office address or meeting room"
                  className="border pl-10"
                  disabled={isSaving}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-overline">
                Interviewers
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-caption border"
                onClick={handleAddInterviewer}
                disabled={isSaving}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add
              </Button>
            </div>
            <div className="space-y-2">
              {interviewers.map((interviewer, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <UsersIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      value={interviewer}
                      onChange={(e) => handleInterviewerChange(index, e.target.value)}
                      placeholder="Interviewer name"
                      className="border pl-10"
                      disabled={isSaving}
                    />
                  </div>
                  {interviewers.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-10 w-10 p-0 border"
                      onClick={() => handleRemoveInterviewer(index)}
                      disabled={isSaving}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="interview-notes" className="text-overline">
              Notes
            </Label>
            <Textarea
              id="interview-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any preparation notes or special instructions..."
              className="border min-h-[100px] resize-y"
              disabled={isSaving}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="border"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            className="border"
            style={{ boxShadow: 'var(--shadow-warm-sm)' }}
            onClick={handleSave}
            disabled={isSaving || !isFormValid}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scheduling...
              </>
            ) : (
              'Schedule Interview'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
